import { useId } from 'react';
import { MinusCircle, PlusCircle } from 'lucide-react';

import { centavosParaDecimal, formatarCentavos, paraCentavos } from '@/nucleo/dinheiro';
import { cn } from '@/nucleo/utilitarios';
import { Button } from '@/ui/base/button';
import { Input } from '@/ui/base/input';

interface MoneyInputProps {
  /** Valor atual, na forma canonica do banco ("1234.56"). Vazio = sem valor. */
  value: string;
  /**
   * Recebe o valor na forma do banco ("1234.56"), pronto para enviar ao
   * servidor — nao a string formatada da tela. Vazio quando o campo esvazia.
   */
  onChange: (valorDecimal: string) => void;
  id?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  'aria-label'?: string;
  /**
   * Permite valor negativo (ex.: dedução/estorno). Quando ativo, mostra um
   * botão de alternar sinal ao lado do campo — o mecanismo de "empurrar
   * dígito" não comporta digitar "-" direto no meio dos centavos.
   */
  allowNegative?: boolean;
}

/**
 * Edicao de dinheiro em real. Mostra "R$ 1.234,56" e emite "1234.56".
 *
 * Trabalha em CENTAVOS INTEIROS: cada digito digitado empurra o valor uma casa,
 * como numa maquina registradora. Assim nao existe estado intermediario
 * invalido nem erro de float, e o cursor nunca "briga" com a virgula.
 *
 * Dinheiro nunca e campo numerico cru (docs/DESIGN-SYSTEM.md, secao 7).
 */
export function MoneyInput({
  value,
  onChange,
  id,
  placeholder,
  disabled,
  className,
  'aria-label': ariaLabel,
  allowNegative,
}: MoneyInputProps) {
  const idGerado = useId();
  const centavosComSinal = paraCentavos(value);
  const negativo = allowNegative && (centavosComSinal ?? 0) < 0;
  const centavos = centavosComSinal === null ? null : Math.abs(centavosComSinal);
  const exibido = centavos === null ? '' : formatarCentavos(centavos);

  const aoDigitar = (texto: string) => {
    // So os digitos importam. O usuario "empurra" centavos da direita — o
    // sinal fica por fora, no botao de alternar (ver abaixo).
    const digitos = texto.replace(/\D/g, '');
    if (digitos === '') {
      onChange('');
      return;
    }
    // Limita a numeric(14,2): 12 inteiros + 2 decimais = 14 digitos.
    const limitado = digitos.slice(0, 14);
    const novosCentavos = Number(limitado);
    onChange(centavosParaDecimal(negativo ? -novosCentavos : novosCentavos));
  };

  const aoAlternarSinal = () => {
    if (centavos === null) return;
    onChange(centavosParaDecimal(negativo ? centavos : -centavos));
  };

  const campo = (
    <Input
      id={id ?? idGerado}
      inputMode="numeric"
      value={exibido}
      placeholder={placeholder ?? 'R$ 0,00'}
      disabled={disabled}
      aria-label={ariaLabel}
      onChange={(e) => aoDigitar(e.target.value)}
      className={cn('text-right font-mono tabular-nums', className)}
    />
  );

  if (!allowNegative) {
    return campo;
  }

  return (
    <div className="flex items-center gap-1.5">
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="h-11 w-11 shrink-0"
        onClick={aoAlternarSinal}
        disabled={disabled}
        aria-label={negativo ? 'Valor negativo — clique para tornar positivo' : 'Valor positivo — clique para tornar negativo'}
        aria-pressed={negativo}
      >
        {negativo ? <MinusCircle className="h-4 w-4 text-destructive" aria-hidden /> : <PlusCircle className="h-4 w-4" aria-hidden />}
      </Button>
      {campo}
    </div>
  );
}
